import Image from "next/image";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

type SectionHeaderProps = {
  chapter?: string;
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
  dark?: boolean;
  headingLevel?: "h1" | "h2" | "h3";
  /** Plain-text label for screen readers when `title` uses styled fragments. */
  headingAriaLabel?: string;
  className?: string;
};

export function RomaSectionHeader({
  chapter,
  eyebrow,
  title,
  description,
  dark = false,
  headingLevel = "h2",
  headingAriaLabel,
  className,
}: SectionHeaderProps) {
  const accent = dark ? "text-[var(--roma-saffron)]" : "text-[var(--roma-terracotta)]";
  const rule = dark ? "bg-[var(--roma-saffron)]/70" : "bg-[var(--roma-terracotta)]/60";
  const Heading = headingLevel;

  return (
    <div className={cn("max-w-3xl", className)}>
      <div className={cn("flex items-center gap-3", accent)}>
        {chapter ? <span className="font-serif text-2xl italic">{chapter}</span> : null}
        <span className={cn("h-px w-8", rule)} />
        <span className="roma-eyebrow"><span>{eyebrow}</span></span>
      </div>
      <Heading
        aria-label={headingAriaLabel}
        className={cn(
          "mt-4 font-serif text-3xl font-normal leading-[0.98] tracking-tight md:text-5xl lg:text-6xl",
          dark ? "text-[var(--primary-foreground)]" : "text-[var(--foreground)]"
        )}
      >
        {title}
      </Heading>
      {description ? (
        <p
          className={cn(
            "mt-5 max-w-2xl font-serif text-lg italic leading-[1.55] md:text-xl",
            dark ? "text-[var(--primary-foreground)]/76" : "text-[var(--foreground)]/72"
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}

type SubPageHeroProps = {
  eyebrow: string;
  title: ReactNode;
  description: ReactNode;
  image?: string;
  imageAlt?: string;
  dark?: boolean;
  headingLevel?: "h1" | "h2" | "h3";
  /** Plain-text label for screen readers when `title` uses styled fragments. */
  headingAriaLabel?: string;
  children?: ReactNode;
  className?: string;
};

export function RomaSubPageHero({
  eyebrow,
  title,
  description,
  image,
  imageAlt = "",
  dark = false,
  headingLevel = "h2",
  headingAriaLabel,
  children,
  className,
}: SubPageHeroProps) {
  return (
    <section
      className={cn(
        "relative isolate overflow-hidden",
        dark ? "roma-paper-dark roma-grain" : "roma-paper roma-grain",
        className
      )}
    >
      {image ? (
        <div className="absolute inset-0">
          <Image
            src={image}
            alt={imageAlt}
            fill
            priority
            quality={85}
            sizes="100vw"
            className="object-cover"
          />
          <div
            className={cn(
              "absolute inset-0",
              dark
                ? "bg-gradient-to-r from-[var(--roma-espresso)] via-[var(--roma-espresso)]/82 to-[var(--roma-espresso)]/38"
                : "bg-gradient-to-r from-[var(--roma-ivory)] via-[var(--roma-ivory)]/86 to-[var(--roma-ivory)]/58"
            )}
          />
        </div>
      ) : null}
      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-5 py-20 sm:px-8 md:py-24 lg:px-12">
        <RomaSectionHeader
          eyebrow={eyebrow}
          title={title}
          description={description}
          dark={dark}
          headingLevel={headingLevel}
          headingAriaLabel={headingAriaLabel}
        />
        {children ? <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">{children}</div> : null}
      </div>
    </section>
  );
}
